import { compact, countBy, sortBy, uniq } from 'lodash';

import { IAvailableCandidate } from '@types';
import { SeniorityOrder } from '@constants';
import {
  availabilityOf,
  AvailableCategoryLabel,
  availableCandidateSkills,
} from './availableCandidates';
import { scoreOf } from './candidate';
import { CandidateRegion, getCandidateRegion } from './candidateRegion';
import {
  isSenioritySelected,
  regionOptions,
  seniorityOptions,
} from './candidateFilters';
import { formatSkillLabel } from './skill';
import { includesQuery } from './string';

export type AvailableFromFilter = 'any' | 'immediately' | 'within30';

export type AvailableProfileOption = 'fullTime' | 'ranked';

export interface IAvailableCandidateFilters {
  categories: IAvailableCandidate['category'][];
  skills: string[];
  primarySkills: string[];
  seniorities: string[];
  regions: CandidateRegion[];
  availableFrom: AvailableFromFilter;
  profile: AvailableProfileOption[];
  ttMin: number | null;
}

const matchesProfileOption = (
  candidate: IAvailableCandidate,
  option: AvailableProfileOption,
): boolean =>
  option === 'fullTime'
    ? (candidate.allocation ?? 100) >= 100
    : Boolean(candidate.ranking?.technicalInterview);

const matchesAvailableFrom = (
  candidate: IAvailableCandidate,
  filter: AvailableFromFilter,
): boolean => {
  if (filter === 'any') return true;

  const { isImmediate, isWithin30Days } = availabilityOf(candidate);

  return filter === 'immediately' ? isImmediate : isWithin30Days;
};

const matchesSeniority = (
  seniority: string | null,
  selected: string[],
  scale: string[],
): boolean =>
  seniority
    ? isSenioritySelected(selected, seniority)
    : scale.every((value) => isSenioritySelected(selected, value));

export const applyAvailableCandidateFilters = (
  candidates: IAvailableCandidate[],
  query: string,
  filters: IAvailableCandidateFilters,
  seniorityScale: string[],
): IAvailableCandidate[] =>
  candidates.filter((candidate) => {
    const skills = availableCandidateSkills(candidate);
    const score = scoreOf(candidate.testTask);

    return (
      includesQuery(query, [
        candidate.name,
        ...skills.flatMap((skill) => [skill, formatSkillLabel(skill)]),
      ]) &&
      (!filters.categories.length ||
        filters.categories.includes(candidate.category)) &&
      filters.skills.every((skill) => skills.includes(skill)) &&
      (!filters.primarySkills.length ||
        candidate.primarySkills.some((skill) =>
          filters.primarySkills.includes(skill),
        )) &&
      (!filters.seniorities.length ||
        matchesSeniority(
          candidate.seniority,
          filters.seniorities,
          seniorityScale,
        )) &&
      (!filters.regions.length ||
        filters.regions.includes(getCandidateRegion(candidate.country))) &&
      matchesAvailableFrom(candidate, filters.availableFrom) &&
      filters.profile.every((option) =>
        matchesProfileOption(candidate, option),
      ) &&
      (filters.ttMin === null || (score !== null && score >= filters.ttMin))
    );
  });

export const AvailableFromLabel: Record<AvailableFromFilter, string> = {
  any: 'Any time',
  immediately: 'Immediately',
  within30: 'Within 30 days',
};

export const AvailableProfileLabel: Record<AvailableProfileOption, string> = {
  fullTime: 'Full-time allocation only',
  ranked: 'Has technical ranking',
};

const sortedValues = (values: (string | null)[]): string[] =>
  compact(uniq(values)).sort((a, b) => a.localeCompare(b));

export const collectAvailableFilterOptions = (
  candidates: IAvailableCandidate[],
) => {
  const counts = countBy(candidates, 'category');

  return {
    categories: (
      Object.keys(AvailableCategoryLabel) as IAvailableCandidate['category'][]
    )
      .filter((category) => counts[category])
      .map((category) => ({ category, count: counts[category] })),
    skills: uniq(candidates.flatMap(availableCandidateSkills)).sort((a, b) =>
      a.localeCompare(b),
    ),
    primarySkills: sortedValues(
      candidates.flatMap((candidate) => candidate.primarySkills),
    ),
    seniorityScale: sortBy(
      compact(uniq(candidates.map((candidate) => candidate.seniority))),
      (seniority) => SeniorityOrder.indexOf(seniority),
    ),
    seniorities: seniorityOptions(
      candidates.map((candidate) => candidate.seniority),
    ),
    regions: regionOptions(candidates.map((candidate) => candidate.country)),
    availableFrom: (
      Object.keys(AvailableFromLabel) as AvailableFromFilter[]
    ).filter((filter) =>
      candidates.some((candidate) => matchesAvailableFrom(candidate, filter)),
    ),
    profile: (
      Object.keys(AvailableProfileLabel) as AvailableProfileOption[]
    ).filter((option) =>
      candidates.some((candidate) => matchesProfileOption(candidate, option)),
    ),
    hasScores: candidates.some(
      (candidate) => scoreOf(candidate.testTask) !== null,
    ),
  };
};

export type AvailableFilterOptions = ReturnType<
  typeof collectAvailableFilterOptions
>;
